import React, { useCallback, useContext, useMemo, useRef } from 'react'

import { EditableComponent } from '@adobe/aem-react-editable-components'
import { ModelManager } from '@adobe/aem-spa-page-model-manager'

import {
  MiniCart,
  ProductGridList as Pgl,
  StickyBarCartSummary,
  MessageStatus,
  Dialog as UIFoundationsDialog,
  Container as UIFoundationsContainer,
  ContainerRoles
} from '@dcxwam/dcx-wam-ui-foundations'

import { useCookies } from 'react-cookie'

import { useLocalStorageCart } from '../../../services/product/use-cart-local-storage'
import { UserDataContext } from '../../../services/user/user-data'
import withComponentRefresh from '../../../utils/aem/editor/withComponentRefresh'
import { useExtractEditableProps } from '../../../utils/aem/editor/extractEditableProps'
import {
  withErrorBoundary,
  ErrorFallback
} from '../../functional/ErrorBoundary'
import {
  GIGYA_COOKIE_JWT,
  GIGYA_COOKIE_UID,
  HYBRIS_ACCESS_TOKEN
} from '../../../utils/constants'
import { useIsSsr } from '../../../utils/isSsr'
import { useParams, useRouter } from '../../../utils/navigation'
import { PageConfig } from '../../../utils/page-config'
import type {
  AppConfig,
  CbxConfigType,
  CurrencyConfig
} from '../../../utils/page-config'
import type { FormatPriceOptions } from '../../../utils/format-price'
import type { EditableComponentPropsType } from '../../../utils/prop-types'

import { handleAddToCart } from '../common/cart-management/handleAddToCart'
import type { HandleAddToCartProps } from '../common/cart-management/handleAddToCart'
import CartErrorMessageDialog from '../common/cart-management/CartErrorMessageDialog'
import { isEditAutoDeliveryCart, sortItemsByPrice } from './utils'

import { useUserLoginInfo } from '../../../utils/use-user-login-info'
import Container from '../../templates/Container/Container'
import {
  type GridListFilterConfig,
  type GroupedItem,
  type ProductListProps
} from './models'
import {
  useAddToCart,
  useAnalytics,
  useAutoDelivery,
  useAutoDeliveryProductImages,
  useDialogs,
  useFilters,
  useMiniCart,
  useProductData,
  useStocks
} from './hooks'
import ExperienceFragmentContent from '../../templates/ExperienceFragmentContent/ExperienceFragmentContent'

import {
  buildVariantData as buildVariantDataFn,
  type VariantDataContext,
  type BuildVariantDataParams,
  buildProductItem as buildProductItemFn,
  type ProductItemContext
} from './builders'
import { getServerSideProps, getHeadData } from './utils/server-side'

type ProductGridListProps = React.ComponentProps<typeof Pgl>

const AUTO_DELIVERY_STICKY_BAR_ID = 'auto-delivery-sticky-bar'

const ProductList: React.FC<EditableComponentPropsType<ProductListProps>> =
  withComponentRefresh<ProductListProps>((props) => {
    const isSsr = useIsSsr()
    const { isUserLoggedIn, uid, jwt } = useUserLoginInfo()
    const userData = useContext(UserDataContext)
    const params = useParams()
    // Narrow the model store data instead of using any
    interface AemModelStoreData {
      config?: { locale?: string }
    }
    const modelStoreData =
      ModelManager.modelStore.getData() as AemModelStoreData
    const pageLocale = modelStoreData?.config?.locale ?? ''

    const currentParams = params || {}
    const { push, replace } = useRouter()

    const caConfig =
      // @ts-expect-error - PageConfig.getConfig() signature doesn't match expected parameter type
      (PageConfig?.getConfig(PageConfig?.ConfigObjects as undefined) ||
        {}) as Pick<AppConfig, 'market' | 'muleSoftConfig' | 'cbxConfig'> & {
        lang?: string
      }

    const cbxConfig = (PageConfig?.getConfig(
      PageConfig?.ConfigObjects?.CbxConfig
    ) || {}) as CbxConfigType

    const currencyConfig = PageConfig.getConfig(
      PageConfig?.ConfigObjects?.CurrencyConfig || {}
    ) as CurrencyConfig

    const formatPriceOptions: FormatPriceOptions | undefined = useMemo(
      () =>
        currencyConfig
          ? {
              currencySymbol: currencyConfig.currencySymbol,
              fractionDigits: currencyConfig.fractionDigits,
              priceFormat: currencyConfig.priceFormat
            }
          : undefined,
      [currencyConfig]
    )

    const gridListFilterConfig = (
      PageConfig.getConfig() as {
        gridListFilterConfig: GridListFilterConfig
      }
    )?.gridListFilterConfig

    const {
      productRootPath = '',
      config,
      general,
      attributeSelector: attributeSelectorsProp = [],
      filters,
      groupBy,
      shopConfig,
      autoDeliveryConfig,
      initialData,
      productReplacementConfig,
      ...rest
    } = { ...props.model }

    const appConfig = PageConfig.getConfig() as AppConfig
    const autoDeliveryPlp = appConfig?.autoDeliveryPlp

    // DCXWAM-45259: On the Auto Delivery PLP in the EDIT auto delivery flow,
    // skip the stock/availability API call and render every product as in stock
    const isEditAutoDeliveryFlow = useMemo(
      () => Boolean(autoDeliveryPlp) && isEditAutoDeliveryCart(),
      [autoDeliveryPlp]
    )

    const resolvedGeneral = useMemo(
      () =>
        productReplacementConfig
          ? { ...general, pricesVisibility: false }
          : general,
      [general, productReplacementConfig]
    )

    const extraDescriptionXFContent =
      resolvedGeneral?.extraDescriptionInfoXF ? (
        <ExperienceFragmentContent
          model={{
            xfPath: `${resolvedGeneral?.extraDescriptionInfoXF}`,
            xfContent: { ...resolvedGeneral?.extraDescriptionInfoXFContent }
          }}
        />
      ) : null

    const lendingConfigurationCode = (
      resolvedGeneral as { lendingConfigurationCode?: string }
    )?.lendingConfigurationCode

    const editableProps = useExtractEditableProps(props, rest)

    const containerRef = useRef(null)

    const [cookies, setCookie] = useCookies([
      GIGYA_COOKIE_UID,
      HYBRIS_ACCESS_TOKEN,
      GIGYA_COOKIE_JWT
    ])

    // "AddToCart" button party
    const { data } = useProductData({
      config,
      superTags: resolvedGeneral?.superTags,
      groupBy,
      initialData,
      productReplacementConfig
    })
    const {
      isButtonDisabled,
      setIsButtonDisabled,
      variantAssetsByAddToCart,
      setVariantAssetsByAddToCart,
      variantCodeByAddToCart,
      setVariantCodeByAddToCart,
      quantitiesSelected,
      setQuantitiesSelected,
      multibuySelections,
      setMultibuySelections,
      activeVariantByCard,
      setActiveVariantByCard,
      errorMessageContent,
      setErrorMessageContent,
      autoDeliveryActionLock
    } = useAddToCart({
      errorMessageDefault: shopConfig?.addToCartErrorMessage
    })
    const { stocks, setUpdatedStocks } = useStocks({
      config,
      gcodes: data?.gcodes,
      ignoreStock: resolvedGeneral?.ignoreStock || isEditAutoDeliveryFlow,
      setIsButtonDisabled
    })

    const [cartLocalStorageJSON, setCartLocalStorage] = useLocalStorageCart()

    const isAutoDeliveryEnabled = Boolean(autoDeliveryPlp)

    const {
      setDialogErrorMessageIsOpen,
      setDialogNotifyMeIsOpen,
      notifyMeContent,
      isAutoDeliveryUneligibilityDialogOpen,
      setIsAutoDeliveryUneligibilityDialogOpen,
      autoDeliveryUneligibilityDialogContent,
      setAutoDeliveryUneligibilityDialogContent,
      configCartErrorMessageDialog,
      cNotifyMeDialog,
      autoDeliveryUneligibilityDialog
    } = useDialogs({
      shopConfig,
      autoDeliveryConfig,
      errorMessageContent
    })

    const {
      filtersStash,
      historyFilters,
      sort,
      filtersData,
      sortData,
      dialogButtonsCTA,
      disableInteractions
    } = useFilters({
      filters,
      gridListFilterConfig,
      data,
      currentParams,
      paramsReady: Boolean(params),
      push,
      replace,
      isSsr,
      containerRef
    })

    const productImageCache = useAutoDeliveryProductImages({
      isAutoDeliveryEnabled,
      data,
      cartLocalStorageJSON,
      config
    })

    const {
      resolvedOrderType,
      isAutoDeliveryMaximumReached,
      disabledCategoryIds,
      autoDeliveryCartEntries,
      stickyBarItems,
      stickyItemsCount,
      stickyBarTitle,
      stickyBarTotalPrice,
      stickyBarTotalValueLabel,
      stickyBarValidation,
      productEntry,
      miniCartTotalUnitCount,
      cartLocalStorageDataForAddToCart
    } = useAutoDelivery({
      isAutoDeliveryEnabled,
      autoDeliveryConfig,
      config,
      cartLocalStorageJSON,
      data,
      isUserLoggedIn,
      uid,
      jwt,
      setIsButtonDisabled,
      pageLocale,
      caConfig,
      general: resolvedGeneral,
      setIsAutoDeliveryUneligibilityDialogOpen,
      setAutoDeliveryUneligibilityDialogContent,
      productCount: data?.items?.length ?? 0,
      productImageCache,
      formatPriceOptions
    })

    const { setActiveMiniCart, configMiniCart } = useMiniCart({
      productEntry,
      miniCartTotalUnitCount,
      variantAssetsByAddToCart,
      shopConfig,
      showOldPrices: resolvedGeneral?.showOldPrices,
      cartLocalStorageJSON,
      variants: data?.items?.flatMap((item) => item.variants) ?? [],
      showOnlyProductSetInMiniCart: (
        shopConfig as { showOnlyProductSetInMiniCart?: boolean }
      )?.showOnlyProductSetInMiniCart
    })

    const triggerAutoDeliveryAdd = useCallback(
      async (
        addToCartConfig: HandleAddToCartProps,
        {
          productQuantity = 1,
          enforceMaximum = true
        }: {
          productQuantity?: number
          enforceMaximum?: boolean
        } = {}
      ) => {
        if (
          !isAutoDeliveryEnabled ||
          (enforceMaximum && isAutoDeliveryMaximumReached) ||
          isButtonDisabled ||
          autoDeliveryActionLock.current
        ) {
          return
        }

        autoDeliveryActionLock.current = true
        try {
          await handleAddToCart({
            ...addToCartConfig,
            productQuantity,
            showMiniCartOnSuccess: false
          })
        } finally {
          autoDeliveryActionLock.current = false
        }
      },
      [isAutoDeliveryEnabled, isAutoDeliveryMaximumReached, isButtonDisabled]
    )

    const sortedItemsByPrice = useMemo(
      () => data?.items?.slice().sort(sortItemsByPrice(sort)),
      [data?.items, sort]
    )

    const { onVariantLink, onVariantColorChange } = useAnalytics({
      sortedItemsByPrice,
      dataLayerListName: resolvedGeneral?.dataLayerListName,
      isSsr,
      containerRef
    })

    const variantDataContext: VariantDataContext = useMemo(
      () => ({
        general: resolvedGeneral,
        productRootPath,
        shopConfig,
        config,
        pageLocale,
        formatPriceOptions,
        stocks,
        isSsr,
        attributeSelectorsProp,
        caConfig,
        cbxConfig,
        multibuySelections,
        quantitiesSelected,
        variantCodeByAddToCart,
        isButtonDisabled,
        isAutoDeliveryEnabled,
        isAutoDeliveryMaximumReached,
        disabledCategoryIds,
        autoDeliveryCartEntries,
        autoDeliveryConfig,
        resolvedOrderType,
        lendingConfigurationCode,
        userData,
        cookies: cookies as VariantDataContext['cookies'],
        setCookie: setCookie as VariantDataContext['setCookie'],
        cartLocalStorageDataForAddToCart,
        setCartLocalStorage,
        onVariantLink,
        triggerAutoDeliveryAdd,
        setIsButtonDisabled,
        setVariantAssetsByAddToCart,
        setVariantCodeByAddToCart,
        setErrorMessageContent,
        setQuantitiesSelected,
        setDialogNotifyMeIsOpen,
        setDialogErrorMessageIsOpen,
        setActiveMiniCart
      }),
      [
        resolvedGeneral,
        productRootPath,
        shopConfig,
        config,
        pageLocale,
        stocks,
        isSsr,
        attributeSelectorsProp,
        caConfig,
        cbxConfig,
        multibuySelections,
        quantitiesSelected,
        variantCodeByAddToCart,
        isButtonDisabled,
        isAutoDeliveryEnabled,
        isAutoDeliveryMaximumReached,
        disabledCategoryIds,
        autoDeliveryCartEntries,
        autoDeliveryConfig,
        resolvedOrderType,
        lendingConfigurationCode,
        userData,
        cookies,
        setCookie,
        cartLocalStorageDataForAddToCart,
        setCartLocalStorage,
        onVariantLink,
        triggerAutoDeliveryAdd,
        setIsButtonDisabled,
        setVariantAssetsByAddToCart,
        setVariantCodeByAddToCart,
        setErrorMessageContent,
        setQuantitiesSelected,
        setDialogNotifyMeIsOpen,
        setDialogErrorMessageIsOpen,
        setActiveMiniCart
      ]
    )

    const buildVariantData = useCallback(
      (variantParams: BuildVariantDataParams) =>
        buildVariantDataFn(variantParams, variantDataContext),
      [variantDataContext]
    )

    const productItemContext: ProductItemContext = useMemo(
      () => ({
        stocks,
        filtersStash,
        historyFilters,
        general: resolvedGeneral,
        attributeSelectorsProp,
        shopConfig,
        pageLocale,
        formatPriceOptions,
        isAutoDeliveryEnabled,
        multibuySelections,
        activeVariantByCard,
        setActiveVariantByCard,
        setMultibuySelections,
        setUpdatedStocks,
        dataGcodes: data?.gcodes || [],
        sortedItemsByPrice,
        onVariantColorChange,
        buildVariantData
      }),
      [
        stocks,
        filtersStash,
        historyFilters,
        resolvedGeneral,
        attributeSelectorsProp,
        shopConfig,
        pageLocale,
        formatPriceOptions,
        isAutoDeliveryEnabled,
        multibuySelections,
        activeVariantByCard,
        setActiveVariantByCard,
        setMultibuySelections,
        setUpdatedStocks,
        data?.gcodes,
        sortedItemsByPrice,
        onVariantColorChange,
        buildVariantData
      ]
    )

    const buildProductItem = useCallback(
      (item: GroupedItem, order: number) =>
        buildProductItemFn(item, order, productItemContext),
      [productItemContext]
    )

    const productList = useMemo(
      () =>
        sortedItemsByPrice ? sortedItemsByPrice.map(buildProductItem) : [],
      [sortedItemsByPrice, buildProductItem]
    )

    const header = useMemo(
      () => ({
        title: resolvedGeneral?.title
          ? `${resolvedGeneral?.title} (${productList?.length || 0})`
          : '',
        description: resolvedGeneral?.description
      }),
      [resolvedGeneral, productList?.length]
    )

    const stickyBarId = useMemo(
      () =>
        props?.id
          ? `${props.id}-${AUTO_DELIVERY_STICKY_BAR_ID}`
          : AUTO_DELIVERY_STICKY_BAR_ID,
      [props?.id]
    )

    return (
      <EditableComponent {...editableProps}>
        <UIFoundationsContainer
          innerRef={containerRef}
          {...(disableInteractions ? { className: 'content-disabled' } : {})}
        >
          <Pgl
            header={header as ProductGridListProps['header']}
            filtersData={filtersData as ProductGridListProps['filtersData']}
            sortData={sortData as ProductGridListProps['sortData']}
            productList={productList as ProductGridListProps['productList']}
            dialogButtons={
              dialogButtonsCTA as ProductGridListProps['dialogButtons']
            }
            extraDescription={
              extraDescriptionXFContent as ProductGridListProps['extraDescription']
            }
          />
          {isAutoDeliveryEnabled && autoDeliveryConfig && (
            <UIFoundationsContainer
              role={ContainerRoles.Status}
              aria-live={stickyBarValidation.message ? 'assertive' : 'off'}
            >
              <StickyBarCartSummary
                id={stickyBarId}
                title={stickyBarTitle}
                itemsCount={stickyItemsCount}
                totalPrice={stickyBarTotalPrice}
                summaryTitle={autoDeliveryConfig?.summaryTitle}
                totalValueLabel={stickyBarTotalValueLabel}
                continueButtonProps={{
                  label: autoDeliveryConfig?.continueCTALabel,
                  linkUrl: autoDeliveryConfig?.continueCTAURL,
                  disable: stickyBarValidation.continueDisabled
                }}
                showMoreLabel={autoDeliveryConfig?.showMoreLabel}
                showLessLabel={autoDeliveryConfig?.showLessLabel}
                items={stickyBarItems}
                warningMessage={stickyBarValidation.message}
                messageStatus={
                  stickyBarValidation.continueDisabled
                    ? MessageStatus.Blocked
                    : MessageStatus.Informational
                }
              />
            </UIFoundationsContainer>
          )}
          {!isAutoDeliveryEnabled && <MiniCart {...configMiniCart} />}
          <UIFoundationsDialog {...cNotifyMeDialog}>
            <Container model={{ ...notifyMeContent }} />
          </UIFoundationsDialog>
          {isAutoDeliveryUneligibilityDialogOpen &&
            Object.keys(autoDeliveryUneligibilityDialogContent).length > 0 && (
              <UIFoundationsDialog {...autoDeliveryUneligibilityDialog}>
                <Container
                  model={{ ...autoDeliveryUneligibilityDialogContent }}
                />
              </UIFoundationsDialog>
            )}
          <CartErrorMessageDialog {...configCartErrorMessageDialog} />
        </UIFoundationsContainer>
      </EditableComponent>
    )
  })

// @ts-expect-error Property 'getServerSideProps' does not exist
ProductList.getServerSideProps = getServerSideProps

// @ts-expect-error Property 'getHeadData' does not exist
ProductList.getHeadData = getHeadData

const ProductListWithErrorBoundary = withErrorBoundary(ProductList, {
  componentName: 'ProductList',
  fallback: <ErrorFallback componentName="ProductList" />
})

export default ProductListWithErrorBoundary
